// apps/web/src/pages/public/Login.tsx

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authCache } from '@/services/auth-cache'
import { toErrorMessage } from '@/utils/errors'
import { post } from '@/services/api'
import type { MeResponse } from '@/types/session'
import { appStore } from '@/store/app'
import { StartOtpResponse, MeResponse as MeResponseSchema } from '@leangap/types'

import EmailForm from './login/EmailForm'
import GoogleSigninButton from '@/components/auth/GoogleSigninButton'
import OtpForm from './login/OtpForm'

// Wrong, expired or over-limit codes and rejected emails are user outcomes, shown inline,
// not bugs (see apps/api/src/routes/auth.ts and errors/twilio.ts).
const OTP_EXPECTED_STATUSES = [400, 404, 429]

export default function PublicLogin() {
	const nav = useNavigate()
	const [step, setStep] = useState<'email' | 'otp'>('email')
	const [email, setEmail] = useState('')
	const [sending, setSending] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [showStartOver, setShowStartOver] = useState(false)
	const [cooldown, setCooldown] = useState(0)
	const [otp, setOtp] = useState(['', '', '', ''])

	useEffect(() => {
		if (cooldown <= 0) return
		const id = window.setInterval(() => setCooldown((c) => c - 1), 1000)
		return () => window.clearInterval(id)
	}, [cooldown])

	const handleEmailSubmit = async (emailValue: string) => {
		setError(null)
		setSending(true)
		try {
			await post('/auth/otp/start', { email: emailValue }, StartOtpResponse, {
				expectedStatuses: OTP_EXPECTED_STATUSES,
			})
			setEmail(emailValue)
			setStep('otp')
			setCooldown(60)
		} catch (e) {
			setError(toErrorMessage(e, 'Failed to send code'))
		} finally {
			setSending(false)
		}
	}

	const handleVerify = async (code: string) => {
		setError(null)
		setSending(true)
		try {
			const resp = await post(
				'/auth/otp/verify',
				{
					email: email.trim(),
					code,
				},
				MeResponseSchema,
				{ expectedStatuses: OTP_EXPECTED_STATUSES }
			)
			const { user } = resp as MeResponse
			authCache.set(user)
			appStore.init(user)
			// Non-draft statuses (Submitted, Admitted, Not Accepted) go to done page
			if (user.application_process !== 'Draft') {
				nav('/account/steps/done', { replace: true })
			} else {
				nav('/account/steps', { replace: true })
			}
		} catch (e) {
			setError(toErrorMessage(e, 'Invalid or expired code'))
			setShowStartOver(true)
		} finally {
			setSending(false)
		}
	}

	const handleResend = async () => {
		if (cooldown > 0) return
		setError(null)
		setShowStartOver(false)
		setSending(true)
		try {
			await post('/auth/otp/start', { email }, StartOtpResponse, {
				expectedStatuses: OTP_EXPECTED_STATUSES,
			})
			setCooldown(60)
		} catch (e) {
			setError(toErrorMessage(e, 'Failed to resend code'))
			setShowStartOver(true)
		} finally {
			setSending(false)
		}
	}

	return step === 'email' ? (
		<>
			<EmailForm onSubmit={handleEmailSubmit} sending={sending} serverError={error} />
			{/* Divider with centered OR */}
			<div className="mt-6 flex items-center gap-4">
				<hr className="flex-1 border-t border-zinc-600" />
				<span className="text-sm text-zinc-600">OR</span>
				<hr className="flex-1 border-t border-zinc-600" />
			</div>
			{/* Large pill-style Google button (component) */}
			<div className="mt-6 flex items-center justify-center">
				<GoogleSigninButton disabled={sending} />
			</div>
		</>
	) : (
		<OtpForm
			email={email}
			otp={otp}
			setOtp={setOtp}
			onVerify={handleVerify}
			sending={sending}
			error={error}
			cooldown={cooldown}
			onResend={handleResend}
			showStartOver={showStartOver}
			onBack={() => {
				setStep('email')
				setOtp(['', '', '', ''])
				setError(null)
				setShowStartOver(false)
			}}
		/>
	)
}
